import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";

/**
 * Identity boundary for one governed upload attempt.
 *
 * `libraryLifecycleGenerationId` is populated when the upload API exposes it;
 * until then `uploadAcceptedAt` + `documentVersionId` scope the attempt.
 */
export type UploadAttemptLifecycle = {
  documentId: string;
  documentVersionId: string;
  uploadAcceptedAt: string;
  libraryLifecycleGenerationId?: number;
};

const FORWARD_ARTIFACT_VERSION_STATUSES = new Set([
  "source_recorded",
  "parsing",
  "chunked",
  "structure_chunked",
  "embedded",
  "vector_indexed",
  "indexed",
  "verified",
]);

export function createUploadAttemptLifecycle(input: {
  documentId: string;
  documentVersionId: string;
  uploadAcceptedAt?: string;
  libraryLifecycleGenerationId?: number;
}): UploadAttemptLifecycle {
  return {
    documentId: input.documentId,
    documentVersionId: input.documentVersionId,
    uploadAcceptedAt: input.uploadAcceptedAt ?? new Date().toISOString(),
    ...(input.libraryLifecycleGenerationId !== undefined
      ? { libraryLifecycleGenerationId: input.libraryLifecycleGenerationId }
      : {}),
  };
}

export function isUploadAttemptLifecycleMatch(
  document: GovernedLibraryDocumentSummary,
  lifecycle: UploadAttemptLifecycle,
): boolean {
  return (
    document.documentId === lifecycle.documentId &&
    document.currentVersionId === lifecycle.documentVersionId
  );
}

/**
 * True when `terminal_failed` is a stale projection from a prior library lifecycle
 * while the artifact for the current upload attempt is still progressing or succeeded.
 */
export function isStaleHistoricalTerminalFailure(
  document: GovernedLibraryDocumentSummary,
  lifecycle: UploadAttemptLifecycle | null | undefined,
): boolean {
  if (document.canonicalState !== "terminal_failed") {
    return false;
  }
  if (document.versionStatus === "failed") {
    return false;
  }
  if (!lifecycle || !isUploadAttemptLifecycleMatch(document, lifecycle)) {
    return false;
  }
  if (
    document.versionStatus &&
    FORWARD_ARTIFACT_VERSION_STATUSES.has(document.versionStatus)
  ) {
    return true;
  }
  if (!document.versionStatus) {
    return true;
  }
  return false;
}

/**
 * Strip stale historical terminal projection so upload UX follows the current attempt.
 */
export function resolveUploadScopedDocumentSummary(
  document: GovernedLibraryDocumentSummary | null | undefined,
  lifecycle: UploadAttemptLifecycle | null | undefined,
): GovernedLibraryDocumentSummary | null | undefined {
  if (!document || !isStaleHistoricalTerminalFailure(document, lifecycle)) {
    return document;
  }
  const { canonicalState: _ignoredCanonicalState, ...withoutCanonical } =
    document;
  return withoutCanonical;
}

export function resolveUploadLifecycleForDocument(
  documentId: string,
  lifecycles: ReadonlyMap<string, UploadAttemptLifecycle>,
): UploadAttemptLifecycle | undefined {
  return lifecycles.get(documentId);
}
